import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, adminHeaders } from "@/lib/apiClient";
import { useAdmin } from "@/context/AdminContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ArrowLeft, Save, Lock } from "lucide-react";
import { toast } from "sonner";

const FIELDS = [
  { key: "cognome", label: "Cognome" },
  { key: "nome", label: "Nome" },
  { key: "anno_nascita", label: "Anno di nascita" },
  { key: "societa", label: "Società" },
  { key: "visita_medica", label: "Visita medica" },
  { key: "scadenza_visita", label: "Scadenza visita", type: "date" },
  { key: "ci_nr", label: "C.I. nr." },
  { key: "scadenza_ci", label: "Scadenza C.I.", type: "date" },
];

export default function SchedaAtleta() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAdmin();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/athletes/${id}`).then((r) => setForm(r.data));
  }, [id]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/athletes/${id}`, form, { headers: adminHeaders() });
      toast.success("Scheda aggiornata");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  if (!form) return <div className="text-muted-foreground p-8">Caricamento...</div>;

  return (
    <div data-testid="scheda-atleta-page" className="max-w-3xl">
      <button
        onClick={() => navigate("/atleti")}
        data-testid="back-to-list-btn"
        className="flex items-center gap-2 text-muted-foreground hover:text-rugby-yellow transition-colors mb-6 font-head uppercase tracking-wide text-xs font-bold"
      >
        <ArrowLeft className="h-4 w-4" /> Elenco Atleti
      </button>

      <div className="flex items-start justify-between gap-4 mb-8 border-b border-border pb-6">
        <div className="flex items-center gap-5">
          <div className="h-20 w-20 bg-rugby-yellow flex items-center justify-center font-head font-bold text-black text-3xl -skew-x-6">
            {(form.cognome?.[0] || "") + (form.nome?.[0] || "")}
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-rugby-yellow mb-1">
              Scheda Anagrafica · #{form.nr ?? "—"}
            </div>
            <h1 className="font-head font-bold uppercase tracking-tighter text-4xl leading-none">
              {form.cognome} {form.nome}
            </h1>
          </div>
        </div>
      </div>

      {!isAdmin && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground bg-white/5 border border-border px-4 py-3 mb-6">
          <Lock className="h-4 w-4 text-rugby-yellow" />
          Sblocca la modalità modifica per aggiornare i dati.
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {FIELDS.map((f) => (
          <div key={f.key} className="space-y-2">
            <Label className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground">
              {f.label}
            </Label>
            <Input
              data-testid={`field-${f.key}`}
              type={f.type || "text"}
              value={form[f.key] || ""}
              disabled={!isAdmin}
              onChange={(e) => update(f.key, e.target.value)}
              className="bg-black/50 border-border focus-visible:ring-rugby-yellow disabled:opacity-70"
            />
          </div>
        ))}

        <div className="space-y-2">
          <Label className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground">
            Tesserato
          </Label>
          <Select
            value={form.tesserato || ""}
            disabled={!isAdmin}
            onValueChange={(v) => update("tesserato", v)}
          >
            <SelectTrigger data-testid="field-tesserato" className="bg-black/50 border-border">
              <SelectValue placeholder="—" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="SI">SI</SelectItem>
              <SelectItem value="NO">NO</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2 md:col-span-2">
          <Label className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground">
            Note
          </Label>
          <Textarea
            data-testid="field-note"
            value={form.note || ""}
            disabled={!isAdmin}
            onChange={(e) => update("note", e.target.value)}
            className="bg-black/50 border-border focus-visible:ring-rugby-yellow disabled:opacity-70"
          />
        </div>
      </div>

      {isAdmin && (
        <div className="mt-8">
          <Button
            onClick={save}
            data-testid="save-athlete-btn"
            disabled={saving}
            className="bg-rugby-yellow text-black hover:bg-rugby-yellow/90 font-head uppercase tracking-wide font-bold"
          >
            <Save className="h-4 w-4 mr-2" />
            {saving ? "Salvataggio..." : "Salva Modifiche"}
          </Button>
        </div>
      )}
    </div>
  );
}
